import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  createOrg,
  errorCode,
  errorMessage,
  requestCode,
  ssoStartUrl,
  ssoStatus,
  verifyCode,
} from './api.ts';
import { phonePlaceholder } from './lib/phone-regions.ts';
import { RegionPicker, workMobileHelp } from './work-mobile.tsx';
import {
  AuthField,
  Button,
  Field,
  Finding,
  GlassPanel,
  LoadError,
  Select,
  Spinner,
} from './ui.tsx';

/**
 * The counted ring as the sign-in hero — a static, decorative rendering of the
 * mark (`brand/mark/mark.svg`), not the live accountability `Ring`, which asserts
 * a number this screen has none of. It is the "icon" the reference-screen formula
 * names, and it maps our own identity onto the halo-and-glyph shape Apple's
 * account pages open with: a soft breath of the accent's light, then the mark.
 *
 * Theme-token colours (ink ring, accent dots) so it holds on both grounds, and
 * `aria-hidden` because the heading already names the product to a screen reader.
 */
function SignInMark() {
  return (
    <div className="relative mx-auto mb-6 h-16 w-16" aria-hidden="true">
      {/* the identity's own light behind the mark */}
      <div
        className="absolute inset-[-40%] rounded-full opacity-70 blur-xl"
        style={{
          background:
            'radial-gradient(circle at center, color-mix(in srgb, var(--accent) 22%, transparent), transparent 70%)',
        }}
      />
      <svg viewBox="0 0 100 100" className="relative h-16 w-16">
        <path
          d="M 75.01 24.10 A 36 36 0 1 1 24.99 24.10"
          fill="none"
          stroke="var(--ink)"
          strokeWidth={10}
          strokeLinecap="round"
        />
        <circle cx="36.51" cy="16.62" r="5.6" fill="var(--accent)" />
        <circle cx="50" cy="14" r="5.6" fill="var(--accent)" />
        <circle cx="63.49" cy="16.62" r="5.6" fill="var(--accent)" />
      </svg>
    </div>
  );
}

/**
 * Sign in.
 *
 * One question at a time — the address, then the code — because more simple
 * screens beat one dense screen, and that inverts the normal instinct to reduce
 * taps (`experience-system.md` §3.2).
 *
 * This is the one surface in the dashboard that holds no data, so it is the one
 * surface that does not owe density (`design-dashboard.md` §7b). It is also the
 * only one somebody reaches on a phone at 3am, which is what the 56px fields
 * and the capsule action are for.
 */
export function SignIn({
  onSignedIn,
  initialError,
}: {
  onSignedIn: () => void;
  /** A sentence from a sign-in attempt that ended before this screen —
   *  an SSO redirect that did not complete lands here with one. */
  initialError?: string | null;
}) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);

  // The SSO handoff is redeemed asynchronously after this screen has already
  // mounted, so a failure arriving later must land in the same error slot the
  // initial value fills.
  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  // Whether this deployment has SSO configured decides whether the Microsoft
  // button exists at all — a button for a door that is not there is the G20
  // class. Until the answer arrives nothing renders, which on a failed fetch
  // degrades to the email flow that always works.
  const sso = useQuery({ queryKey: ['sso'], queryFn: ssoStatus, staleTime: Infinity });

  const send = useMutation({
    mutationFn: () => requestCode(email),
    onSuccess: () => {
      setResent(sent);
      setSent(true);
      setError(null);
    },
    onError: () => setError('Could not send a code. Check the address and try again.'),
  });

  const verify = useMutation({
    mutationFn: () => verifyCode(email, code),
    onSuccess: onSignedIn,
    // The API answers identically for a wrong code and an expired one, so this
    // copy does too rather than inventing a distinction the server refuses to
    // make.
    onError: () => setError('That code was not accepted. Request a new one.'),
  });

  const startOver = () => {
    setSent(false);
    setResent(false);
    setCode('');
    setError(null);
  };

  return (
    <main className="first-light-ground flex min-h-screen items-center justify-center px-6 py-12">
      {/* The frozen signature over the empty screen: blue through a breath of
          gold at the very top of the page. */}
      <div className="horizon-rule" aria-hidden="true" />
      {/* The one surface that holds no data, so the one surface that gets glass
          and the identity's own light behind it. The card floats on the First
          Light ground; the auth-register geometry inside it is unchanged. */}
      <GlassPanel className="w-full max-w-[360px] px-8 py-9">
        {/* Hero, heading and supporting line are centred as one lockup — the
            account-card shape Apple's own sign-in opens with — while the field
            and the capsule below stay full-width and the label floats left. */}
        <SignInMark />

        {/* 28/32 at +0.196px, Apple's own page heading. Positive tracking at
            display size and negative at body size is their tracking curve, not
            an inconsistency. The product is named in the heading rather than in a
            separate eyebrow, so the mark and one clear line carry the identity. */}
        <h1 className="text-center text-[28px] leading-[32px] font-[650] tracking-[0.196px] text-[var(--ink)]">
          {sent ? 'Check your email' : 'Sign in to EVAC'}
        </h1>
        <p className="mx-auto mt-2 mb-8 max-w-[300px] text-center text-[17px] leading-[23px] tracking-[-0.374px] text-[var(--ink-2)]">
          {sent ? (
            <>
              Enter the six-digit code sent to <span className="text-[var(--ink)]">{email}</span>.
            </>
          ) : (
            'We will email you a six-digit code. There is no password to remember or leak.'
          )}
        </p>

        <form
          className="space-y-3.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (sent) verify.mutate();
            else send.mutate();
          }}
        >
          {sent ? (
            <AuthField
              label="Six-digit code"
              value={code}
              onChange={(v) => setCode(v.replace(/\D/g, ''))}
              inputMode="numeric"
              maxLength={6}
              autoFocus
              /* The OS offers the code straight from Mail, which is the whole
                 reason a six-digit code beats a magic link on a phone. */
              autoComplete="one-time-code"
              hint="It expires in ten minutes and works once."
            />
          ) : (
            <AuthField
              label="Email"
              value={email}
              onChange={setEmail}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
            />
          )}

          <div className="pt-1">
            <Button
              type="submit"
              variant="primary"
              size="auth"
              full
              disabled={sent ? code.length < 6 || verify.isPending : !email || send.isPending}
            >
              {sent ? 'Sign in' : 'Send code'}
            </Button>
          </div>

          {error ? <Finding>{error}</Finding> : null}
        </form>

        {!sent && sso.data?.enabled ? (
          <div className="mt-6 flex flex-col items-center">
            {/* A top-level navigation, not a fetch: the flow is a redirect
                dance through the organisation's own identity provider, and
                the callback lands the person back on the path they left. */}
            <Button
              variant="quiet"
              size="auth"
              onClick={() => {
                window.location.href = ssoStartUrl(
                  window.location.pathname + window.location.search,
                );
              }}
            >
              Sign in with Microsoft
            </Button>
          </div>
        ) : null}

        {sent ? (
          <div className="mt-6 flex flex-col items-center gap-1">
            {/* A code that never arrives is usually a typo in the address, so
                the way back to it is on the screen rather than behind a
                reload. */}
            <Button variant="quiet" size="auth" onClick={startOver}>
              Use a different address
            </Button>
            <Button
              variant="quiet"
              size="auth"
              disabled={send.isPending}
              onClick={() => send.mutate()}
            >
              Send a new code
            </Button>
            {resent ? (
              <p className="mt-1 text-center text-[13px] leading-[18px] text-[var(--ink-2)]">
                A new code is on its way. The earlier one no longer works.
              </p>
            ) : null}
          </div>
        ) : null}
      </GlassPanel>
    </main>
  );
}

/**
 * What a signed-in account with no active organisation is shown — decided from
 * the `/auth/me` query's *state*, not from its memberships list alone.
 *
 * "The list loaded and is empty" and "the list never arrived" both leave
 * `memberships` as `[]`, and the dashboard used to read both as the first case:
 * a network error, a CORS refusal or a 5xx put the Owner of an existing
 * organisation in front of "Name the organisation" (observed 2026-09-29 against
 * staging from an origin its CORS did not allow — they then tried to create a
 * second one and were told it could not be created). That is the
 * state-asserting bug class: a load failure presented as a fact about the
 * account. So a failure renders as a failure, with Retry, and org-creation is
 * offered only once the server has actually said there is nothing to list.
 */
export function NoActiveOrg({
  me,
  onSignOut,
}: {
  me: Pick<UseQueryResult<unknown>, 'isPending' | 'isError' | 'isFetching'> & {
    refetch: () => unknown;
  };
  onSignOut: () => void;
}) {
  if (me.isError) {
    return (
      <main className="first-light-ground flex min-h-screen items-center justify-center px-6 py-12">
        <div className="horizon-rule" aria-hidden="true" />
        <GlassPanel className="w-full max-w-[440px] px-8 py-9">
          <h1 className="mb-6 text-[28px] leading-[32px] font-[650] tracking-[0.196px] text-[var(--ink)]">
            Your organisations could not be loaded
          </h1>
          <LoadError onRetry={() => void me.refetch()}>
            {me.isFetching
              ? 'Trying again…'
              : 'The server did not answer. This is a connection or server problem, not an empty account — nothing about your organisations has changed.'}
          </LoadError>
          <div className="mt-6">
            <Button variant="quiet" size="auth" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </GlassPanel>
      </main>
    );
  }
  if (me.isPending) return <Spinner />;
  return <FirstRun onSignOut={onSignOut} />;
}

/**
 * The first thing a fresh account sees: create the organisation they administer.
 *
 * Signing in with an address that has never held a membership used to land at
 * an empty shell reading "This account is not a member of any organisation
 * yet." — a sentence with no next step. Every school, warehouse or venue starts
 * here, so this is the front door of the dashboard for anybody who is not
 * inheriting an existing account.
 *
 * `POST /orgs` makes the caller its Owner (`api/src/routes/orgs.ts`), and the
 * shell re-runs its `/auth/me` query on success — the memberships list stops
 * being empty and the dashboard advances into its normal Home flow. No routing
 * change needed: the empty-state branch in `main.tsx` stops matching.
 *
 * `countryCode` is a real operational commitment — the API stores it on
 * `orgs.country_code` and downstream rules (SES sender configuration, the
 * SMS Sender ID Register, the mandated-drill heartbeat cadence) will read it.
 * A wrong two-letter code is worse than a dropdown that asks. AU is default:
 * every wave-1 pilot is Australian.
 */
export function FirstRun({ onSignOut }: { onSignOut: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('AU');
  const [mobile, setMobile] = useState('');
  /** The number's own country; follows the organisation's until changed. */
  const [mobileRegion, setMobileRegion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * The founder's work mobile (migrations 0138/0139). Required in every
   * country; stored unverified at creation — a code cannot be texted before
   * the organisation exists — and the dashboard's banner asks them to confirm
   * it straight after.
   */
  const region = mobileRegion ?? country;

  const create = useMutation({
    mutationFn: () => createOrg(name.trim(), country, mobile.trim() || undefined, region),
    onSuccess: () => {
      // Refetch `me` so its memberships list is no longer empty — the empty
      // -state branch stops matching and the dashboard advances to Home on its
      // own.
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) =>
      setError(
        errorCode(err) === 'invalid_mobile' || errorCode(err) === 'country_not_supported'
          ? (errorMessage(err) ?? 'Check the mobile number.')
          : 'Could not create the organisation. Check the name and try again.',
      ),
  });
  const missing = !name.trim()
    ? 'Name the organisation to continue.'
    : !mobile.trim()
      ? 'Add your mobile number to continue.'
      : null;

  return (
    <main className="first-light-ground flex min-h-screen items-center justify-center px-6 py-12">
      <div className="horizon-rule" aria-hidden="true" />
      <GlassPanel className="w-full max-w-[440px] px-8 py-9">
        <p className="text-[12px] leading-[16px] font-[650] tracking-[0.06em] text-[var(--ink-3)] uppercase">
          EVAC
        </p>
        <h1 className="mt-3 text-[28px] leading-[32px] font-[650] tracking-[0.196px] text-[var(--ink)]">
          Name the organisation
        </h1>
        <p className="mt-2 mb-8 text-[17px] leading-[23px] tracking-[-0.374px] text-[var(--ink-2)]">
          This is the safety group other people will be invited into. You will be its Owner, so plan
          on staying — ownership can be transferred later.
        </p>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (missing || create.isPending) return;
            setError(null);
            create.mutate();
          }}
        >
          <Field
            label="Organisation name"
            value={name}
            onChange={setName}
            placeholder="Lincoln Primary School"
            autoFocus
            hint="The name that appears on alerts, reports and every screen your team will see."
          />

          <Select
            label="Country"
            value={country}
            onChange={setCountry}
            options={[
              { value: 'AU', label: 'Australia' },
              { value: 'NZ', label: 'New Zealand' },
              { value: 'GB', label: 'United Kingdom' },
              { value: 'US', label: 'United States' },
              { value: 'CA', label: 'Canada' },
              { value: 'IE', label: 'Ireland' },
            ]}
          />

          <RegionPicker value={region} onChange={setMobileRegion} />

          <Field
            label="Your mobile number"
            value={mobile}
            onChange={setMobile}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={phonePlaceholder(region)}
            hint={workMobileHelp('owner')}
          />

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="auth"
              full
              disabled={Boolean(missing) || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create organisation'}
            </Button>
            {missing ? (
              <p className="mt-2 text-[13px] text-[var(--ink-2)]" aria-live="polite">
                {missing}
              </p>
            ) : null}
          </div>

          {error ? <Finding>{error}</Finding> : null}
        </form>

        <div className="mt-6">
          <Button variant="quiet" size="auth" onClick={onSignOut}>
            Sign out instead
          </Button>
        </div>
      </GlassPanel>
    </main>
  );
}
